import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
  TextField,
} from '@mui/material'
import axios from 'axios'
import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useMessage } from '~/src/hooks'
import { invoiceRepository } from '~/src/repositories'
import { IInvoiceDetail, IPatchInvoiceBody } from '~/src/repositories/Invoice/models/Invoice'
import { UploadFile } from './UploadFile'

interface IOptionsInvoiceType {
  id: number
  invoiceType: string
  labelType: string
}

interface IOptionsStatus {
  idStatus: number
  status: string
  statusLabel: string
}

const useInvoiceTypes = (): IOptionsInvoiceType[] => {
  // const { t } = useTranslation()
  return [
    { id: 1, invoiceType: 'PROFORMA', labelType: 'Proforma Invoice' },
    { id: 2, invoiceType: 'SALES', labelType: 'Sales Invoice' },
  ]
}

const useStatuses = (): IOptionsStatus[] => {
  const { t } = useTranslation()
  return [
    { idStatus: 1, status: 'OPEN', statusLabel: t('proforma_invoice.notCompatible') },
    {
      idStatus: 2,
      status: 'IN_PROGRESS',
      statusLabel: t('proforma_invoice.waitingToBeCreated'),
    },
    { idStatus: 3, status: 'CLOSED', statusLabel: t('proforma_invoice.acknowledged') },
  ]
}

interface IInvoiceDetailProps {
  invoice: IInvoiceDetail
}

export const ProformaInvoiceForm: FC<IInvoiceDetailProps> = ({ invoice }) => {
  const navigate = useNavigate()
  const statuses = useStatuses()
  const invoiceTypes = useInvoiceTypes()
  const { t } = useTranslation()
  const { id } = invoice
  const { handleOpenWithSuccessMessage, handleOpenWithErrorMessage } = useMessage()

  const initStatus = invoice.status && statuses.find((s) => s.status === invoice.status)?.idStatus
  const [billCode, setBillCode] = useState(invoice.billCode)
  const [invoiceType, setInvoiceType] = useState(invoice.invoiceType)
  const [status, setStatus] = useState(initStatus as string)
  const [invoiceFile, setInvoiceFile] = useState<{ filename: string; url: string } | undefined>(
    invoice.invoice
  )
  const [isLoading, setIsLoading] = useState(false)

  const handleBillCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBillCode(event.target.value)
  }

  const handleInvoiceTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInvoiceType(event.target.value)
  }

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatus(event.target.value)
  }

  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      const payload: IPatchInvoiceBody = {
        billCode: billCode,
        type: invoiceType,
        status: status,
        invoice: invoiceFile,
      }
      console.log(payload)
      await invoiceRepository.path(Number(id), payload)
      handleOpenWithSuccessMessage(t('proforma_invoice.updated'))
    } catch (e) {
      if (axios.isAxiosError(e)) {
        handleOpenWithErrorMessage(e.message)
      }
    }
  }

  const handleFileChange = async (file: File): Promise<void> => {
    try {
      setIsLoading(true)
      const data = await invoiceRepository.getFile()
      await invoiceRepository.putFileUpload(data.url, file)
      setInvoiceFile({ filename: file.name, url: data.path })
    } catch (e) {
      if (axios.isAxiosError(e)) {
        handleOpenWithErrorMessage(e.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileDelete = () => {
    setInvoiceFile(undefined)
  }

  return (
    <Paper>
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            padding: '40px 61px 40px 61px',
          }}
        >
          <Box display={'flex'} mb={'24px'}>
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.15px',
                color: '#000000',
                width: '136px',
                marginRight: '16px',
              }}
            >
              {t('proforma_invoice.customer')}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.15px',
                color: '#000000',
              }}
            >
              {invoice.customer}
            </Typography>
          </Box>
          <Box display={'flex'} mb={'24px'}>
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '44px',
                letterSpacing: '0.15px',
                color: '#000000',
                width: '136px',
                marginRight: '16px',
              }}
            >
              {t('proforma_invoice.invoice_no.')}
            </Typography>
            <TextField
                id="outlined-basic"
                type="text"
                size={'small'}
                InputLabelProps={{
                  shrink: true,
                }}
                value={billCode as string}
                onChange={handleBillCodeChange}
              />
          </Box>
          <Box display={'flex'} mb={'24px'}>
            <Typography
              variant={'body1'}
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.15px',
                color: '#000000',
                width: '136px',
                marginRight: '16px',
                paddingTop: '10px',
              }}
            >
              {t('proforma_invoice.invoice_type')}
            </Typography>
            <FormControl>
              <RadioGroup
                aria-labelledby="invoice_type-buttons-group-label"
                name="invoice_type"
                value={invoiceType}
                onChange={handleInvoiceTypeChange}
              >
                {invoiceTypes.map((type) => (
                  <FormControlLabel
                    key={type.id}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        lineHeight: '24px',
                        letterSpacing: '0.15px',
                        color: 'rgba(0, 0, 0, 0.87)',
                        fontFamily: 'Roboto',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: 16,
                      },
                    }}
                    value={type.invoiceType}
                    control={<Radio size={'small'} />}
                    label={type.invoiceType}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
          <Box display={'flex'} mb={'24px'}>
            <Typography
              variant={'body1'}
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.15px',
                color: '#000000',
                width: '136px',
                marginRight: '16px',
                paddingTop: '10px',
              }}
            >
              {t('proforma_invoice.status')}
            </Typography>
            <FormControl>
              <RadioGroup
                aria-labelledby="status-buttons-group-label"
                name="status-buttons-group"
                value={status}
                onChange={handleStatusChange}
              >
                {statuses.map((status) => (
                  <FormControlLabel
                    key={status.idStatus}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        lineHeight: '24px',
                        letterSpacing: '0.15px',
                        color: 'rgba(0, 0, 0, 0.87)',
                        fontFamily: 'Roboto',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: 16,
                      },
                    }}
                    value={status.idStatus}
                    control={<Radio size={'small'} />}
                    label={status.statusLabel}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
          <Box display={'flex'}>
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.15px',
                color: '#000000',
                width: '136px',
                marginRight: '16px',
                paddingTop: '10px',
              }}
            >
              {t('proforma_invoice.invoice_upload')}
            </Typography>
            <UploadFile
              onSaveFile={handleFileChange}
              onDeleteFile={handleFileDelete}
              file={invoiceFile}
              isLoading={isLoading}
            />
          </Box>
        </Box>
        <Box
          sx={{
            padding: '12px 16px',
            display: 'flex',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type={'submit'}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: 15,
              letterSpacing: 0.46,
            }}
          >
            {t('save')}
          </Button>
        </Box>
      </form>
    </Paper>
  )
}
